import { DEFAULT_FONT_NAME, DEFAULT_JA_FONT_NAME } from "./const";
import type {
  IDropShadowEffect,
  IRGBA,
  IVector,
  PaddingValue,
  ValueWithUnit,
} from "./type";
import { zeroValue } from "./util";

export const zeroOffset: IVector = { x: 0, y: 0 };

export const FIXED_POPUP_PADDING: ValueWithUnit = { value: 20, unit: "px" };

export const zeroPadding: PaddingValue = {
  bottom: zeroValue,
  top: zeroValue,
  left: zeroValue,
  right: zeroValue,
};

export const black: IRGBA = {
  r: 0,
  g: 0,
  b: 0,
  a: 1,
};
export const transparentBlack: IRGBA = {
  r: 0,
  g: 0,
  b: 0,
  a: 0,
};

export const dropShadowBlackTransparent: IDropShadowEffect = {
  type: "DROP_SHADOW",
  radius: 0,
  offset: zeroOffset,
  color: transparentBlack,
  visible: true,
};

export const shadowSm: IDropShadowEffect[] = [
  dropShadowBlackTransparent,
  dropShadowBlackTransparent,
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 1 },
    radius: 2,
    spread: 0,
    color: { ...black, a: 0.05 },
    visible: true,
  },
];

export const shadow_3_4_5: IDropShadowEffect[] = [
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 0.3 },
    radius: 0.2,
    color: { ...black, a: 0.01 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 1 },
    radius: 0.6,
    color: { ...black, a: 0.015 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 2.1 },
    radius: 1.5,
    color: { ...black, a: 0.02 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 4 },
    radius: 5,
    color: { ...black, a: 0.03 },
    visible: true,
  },
];

export const shadow_5_5_7: IDropShadowEffect[] = [
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 0.2 },
    radius: 0.2,
    color: { ...black, a: 0.014 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 0.7 },
    radius: 0.5,
    color: { ...black, a: 0.02 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 1.3 },
    radius: 0.9,
    color: { ...black, a: 0.025 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 2.1 },
    radius: 1.6,
    color: { ...black, a: 0.03 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 3.2 },
    radius: 2.9,
    color: { ...black, a: 0.036 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 5 },
    radius: 7,
    color: { ...black, a: 0.05 },
    visible: true,
  },
];

export const shadow_7_10_10: IDropShadowEffect[] = [
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 0.4 },
    radius: 0.3,
    color: { ...black, a: 0.02 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 1.3 },
    radius: 0.7,
    color: { ...black, a: 0.028 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 2.6 },
    radius: 1.3,
    color: { ...black, a: 0.035 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 4.3 },
    radius: 2.2,
    color: { ...black, a: 0.042 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 6.5 },
    radius: 4.2,
    color: { ...black, a: 0.05 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 10 },
    radius: 10,
    color: { ...black, a: 0.07 },
    visible: true,
  },
];

export const shadow_9_13_16: IDropShadowEffect[] = [
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 0.5 },
    radius: 0.4,
    color: { ...black, a: 0.025 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 1.7 },
    radius: 1.1,
    color: { ...black, a: 0.036 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 3.4 },
    radius: 2,
    color: { ...black, a: 0.045 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 5.5 },
    radius: 3.6,
    color: { ...black, a: 0.054 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 8.4 },
    radius: 6.7,
    color: { ...black, a: 0.065 },
    visible: true,
  },
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 13 },
    radius: 16,
    color: { ...black, a: 0.09 },
    visible: true,
  },
];

export const textShadowDark: IDropShadowEffect[] = [
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 0 },
    radius: 5,
    spread: 0,
    color: { r: 0, g: 0, b: 0, a: 0.5 },
    visible: true,
  },
];

export const textShadowLight: IDropShadowEffect[] = [
  {
    type: "DROP_SHADOW",
    offset: { x: 0, y: 0 },
    radius: 5,
    color: { r: 255, g: 255, b: 255, a: 0.5 },
    visible: true,
  },
];

export const defaultFontFamily = `${DEFAULT_FONT_NAME}, "Helvetica Neue", "Apple SD Gothic Neo", "Malgun Gothic", "맑은고딕", Dotum, "돋움", Gulim, "굴림", Helvetica, Arial, sans-serif`;
export const defaultEnFontFamily = `${DEFAULT_FONT_NAME}, "Helvetica Neue", "Apple SD Gothic Neo", "Malgun Gothic", "맑은고딕", Dotum, "돋움", Gulim, "굴림", Helvetica, Arial, sans-serif`;
export const defaultJaFontFamily = `${DEFAULT_JA_FONT_NAME}, "ヒラギノ角ゴ Pro W3", "Hiragino Kaku Gothic Pro", "メイリオ", "Meiryo", "MS Pゴシック", "MS PGothic", "Inter", "Helvetica Neue",${DEFAULT_FONT_NAME}, "Apple SD Gothic Neo", "Malgun Gothic", "맑은고딕", Dotum, "돋움", Gulim, "굴림", Helvetica, Arial, sans-serif`;
